export enum ProductServiceType {
  ExpertSetup = 'Expert Setup',
  ServiceContract = 'Service Contract',
  CustomConfiguration = 'Custom Configuration',
}

export enum SignatureStateType {
  Available = 'AVAILABLE',
  Unavailable = 'UNAVAILABLE',
  Forced = 'FORCED',
}

export enum SignatureForcedReasonType {
  OverThreshold = 'OVER_THRESHOLD',
  RequiredState = 'REQUIRED_STATE',
}

export enum ProductConditionRank {
  NEW = 'New',
  OPEN_BOX = 'Open box',
  REMANUFACTURED = 'Remanufactured',
  REFURBISHED = 'Refurbished',
  LIFEXTENDED = 'Life Extended',
}

export enum ShoppingCartStepType {
  ShoppingCart = '/shopping-cart',
}

export enum DiscountErrorType {
  GiftCardError = 'gift_cart_error',
  CouponError = 'coupon_error',
}

export type DiscountErrorState = {
  code: DiscountErrorType;
  message: string;
};

export type AdditionalProductFee = {
  message: string;
  name: string;
  price: number;
  sku: string;
};

export type ServiceInfoProduct = {
  image: string;
  name: string;
  price: number;
  sku: string;
};

export type ServiceInfo = {
  buyButtonAvailable: boolean;
  content: string;
  desc: string;
  editContentUrl: string;
  editServiceUrl: string;
  editable: boolean;
  largeImage: string;
  name: string;
  price: number;
  serviceAdditionalProducts: Array<ServiceInfoProduct>;
  serviceRelatedProducts: Array<ServiceInfoProduct>;
};

export type ProductServiceInfo = {
  name: string;
  price: number;
  desc: string;
  largeImage: string;
};

export type ProductAddonElement = {
  name: string;
  price: number;
  productUrl: string;
  sku?: string;
  smallImage?: string;
  image?: string;
  rating?: number;
  id?: number;
  discountPrice?: number;
  reviewsCount?: number;
  [key: string]: any;
};

export type CustomConfigurationElement = {
  id: number;
  name: string;
  price: number;
  image: string;
  productUrl: string;
  canAddToCart: boolean;
  selected: boolean;
  sku: string;
};

export type ProductCustomConfiguration = {
  name: ProductServiceType;
  seq: number;
  combineAllowed: boolean;
  mainSku: string;
  elements: Array<CustomConfigurationElement>;
};

export type ProductServicesElement = {
  id: number;
  canAddToCart: boolean;
  name: string;
  price: number;
  selected: boolean;
  image: string;
  sku: string;
};

export type ProductServices = {
  name: ProductServiceType;
  combineAllowed: boolean;
  elements: Array<ProductServicesElement>;
  seq?: number;
};

export type ProductAddons = {
  accessoryList: Array<ProductAddonElement>;
  ammoList: Array<ProductAddonElement>;
  customConfigurations: Array<ProductCustomConfiguration>;
  serviceContracts: Array<ProductServices>;
  scopeAccessories: Array<ProductAddonElement>;
};

export type Product = {
  id: string;
  name: string;
  image: string;
  brandItemCode?: string;
  stockDate?: string;
  quantity: number;
  price: number;
  productSpecifications?: Array<string> | null;
  specialOrder?: boolean;
  backordered?: boolean;
  seq: number;
  productUrl: string;
  purchaseLimit?: number;
  purchaseLimitMessage?: string | null;
  editable?: boolean;
  removeAvailable?: boolean;
  superConfigurator?: boolean;
  serviceInfo?: ProductServiceInfo;
  additionalProductFee?: AdditionalProductFee | null;
  services?: Array<ProductServices>;
  addons?: Array<ProductAddons>;
  relatedProductsAvailable?: boolean;
  typeService: boolean;
  sku?: string;
  itemConditionRank?: 'NEW' | 'OPEN_BOX' | 'REMANUFACTURED' | 'REFURBISHED' | 'LIFEXTENDED';
  autoClipCoupon?: AutoClipCoupon | null;
  couponAllowed?: boolean;
};

export type SignatureState = {
  checked: boolean;
  forcedReasonType: SignatureForcedReasonType | null;
  state: SignatureStateType;
};

export type SubOrderVendorShipping = {
  freeShippingAmountLeft: number | null;
  freeShippingAmountMin: number | null;
  freeShippingMessage: string;
  freeShippingZone: string | null;
  shippingOption: number;
  sendAsTheyArrive: boolean;
  signatureState: SignatureState;
};
export type SubOrder = {
  vendor: string;
  vendorId: number;
  orderId: number;
  methodId: number;
  showCanadaRestrictMessage: boolean;
  productsWithoutAirShippingOption?: Array<string>;
  vendorShipping: SubOrderVendorShipping;
  products?: Array<Product>;
};

export type CartSummary = {
  productsAndServices: number;
  subTotal: number;
  giftCard: number | null;
  bullseyeBucks: number | null;
  bundleDiscount: number | null;
  affirmStarting: number;
  totalDue: number;
  couponDiscount: number | null;
  autoApplyCouponResponse?: {
    couponNumber: string;
    autoCouponDiscount: number;
  } | null;
  tax: number;
  state: string;
  country: string;
  additionalProductFees: Array<AdditionalProductFee> | null;
  checkoutButtonAvailable?: string;
};

export type CouponMessage = {
  message: string;
  code: string;
};

export type ShoppingCard = {
  adminUser: boolean;
  applyCouponMessage: CouponMessage | null;
  enabledRecaptcha: boolean;
  notDeliverable: boolean;
  cartItems: number;
  cartSummary: CartSummary;
  subOrders: Array<SubOrder>;
  cookies?: string | null;
  regularCouponApplied: boolean;
  paymentMethod: string;
};

export type GetShareCartLinkResponse = {
  orderKey: string;
  refId: string;
};

export type AutoClipCoupon = {
  couponId: string;
  discount: {
    type: 1 | 2;
    value: number;
  };
  applied: boolean;
};
